import { selectorPayerId } from 'selectors/payerUser';
import PropTypes from 'prop-types';
import {
  BUSINESS_CODE,
  PERSON,
  PAYER_USER_STATUS_ACTIVE,
  ROWS_PER_PAGE_MAX,
} from 'consts';
import { head } from 'ramda';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFSPId } from 'hooks';
import useIsBusy from 'hooks/useIsBusy';
import { load, LOAD as PAYER_USER_LOAD } from 'model/payerUser';
import NoActiveUsersSnackbar from './NoActiveUsersSnackbar';
import PayerButton from './PayerButton';
import PayerUserButton from './PayerUserButton';

const PayerOrPayerUserButton = ({
  fspId: fspIdProp,
  onPayerClick,
  onPayerUserClick,
  payerAction,
  payerButtonText,
  payerId,
  payerType,
  payerUser,
  payerUserAction,
  payerUserButtonText,
  selector,
  startIcon,
  submenu,
  transactAs,
}) => {
  const dispatch = useDispatch();
  const fspId = useFSPId(fspIdProp);
  const items = useSelector((state) => selectorPayerId(state, payerId));
  const isBusiness = payerType === BUSINESS_CODE;
  const isPerson = payerType === PERSON;

  const [awaitingPayerUserFetch, setAwaitingPayerUserFetch] = useState(false);
  const [noActiveUsersOpen, setNoActiveUsersOpen] = useState(false);
  const lastPayerUserClickSurfaceRef = useRef(null);
  const hasSeenPayerUserBusyRef = useRef(false);

  const isPayerUsersBusy = useIsBusy(PAYER_USER_LOAD);

  const needsPersonPayerUserPrefetch =
    isPerson && payerUser == null && items.length === 0 && !isBusiness;

  const showPayerOnlyButton =
    !isBusiness && (items.length === 0 || (items.length === 1 && !isPerson));

  const resolvedPayerUser =
    payerUser ?? (isPerson && items.length === 1 ? head(items) : null);

  const usePayerPrimaryLabel =
    isPerson && items.length === 1 && payerUser == null && resolvedPayerUser;

  const payerUserButtonLabel = usePayerPrimaryLabel
    ? payerButtonText
    : payerUserButtonText;

  useEffect(() => {
    if (awaitingPayerUserFetch && isPayerUsersBusy) {
      hasSeenPayerUserBusyRef.current = true;
    }
  }, [awaitingPayerUserFetch, isPayerUsersBusy]);

  useEffect(() => {
    if (!awaitingPayerUserFetch || isPayerUsersBusy) {
      return;
    }
    if (!hasSeenPayerUserBusyRef.current && items.length === 0) {
      return;
    }

    hasSeenPayerUserBusyRef.current = false;
    setAwaitingPayerUserFetch(false);

    const active = items.filter(
      ({ status }) => status === PAYER_USER_STATUS_ACTIVE
    );

    if (active.length === 0) {
      setNoActiveUsersOpen(true);
      return;
    }
    if (active.length === 1) {
      onPayerUserClick(lastPayerUserClickSurfaceRef.current, active[0]);
    }
  }, [awaitingPayerUserFetch, isPayerUsersBusy, items, onPayerUserClick]);

  const handlePayerOnlyClick = useCallback(
    (event) => {
      if (needsPersonPayerUserPrefetch) {
        event.stopPropagation();
        lastPayerUserClickSurfaceRef.current = {
          currentTarget: event.currentTarget,
          target: event.target ?? event.currentTarget,
        };
        hasSeenPayerUserBusyRef.current = false;
        setAwaitingPayerUserFetch(true);
        dispatch(load({ fspId, payerId, limit: ROWS_PER_PAGE_MAX }));
        return;
      }
      onPayerClick(event);
    },
    [dispatch, fspId, needsPersonPayerUserPrefetch, onPayerClick, payerId]
  );

  const handleNoActiveUsersSnackbarClose = useCallback(() => {
    setNoActiveUsersOpen(false);
  }, []);

  const payerUserFetchLoading = awaitingPayerUserFetch && isPayerUsersBusy;

  return (
    <>
      {showPayerOnlyButton ? (
        <PayerButton
          action={payerAction}
          isLoadingExternal={payerUserFetchLoading}
          onClick={handlePayerOnlyClick}
          payerId={payerId}
          selector={selector}
          startIcon={startIcon}
          text={payerButtonText}
          transactAs={transactAs}
        />
      ) : (
        <PayerUserButton
          action={payerUserAction}
          fspId={fspId}
          onClick={onPayerUserClick}
          payerId={payerId}
          payerUser={resolvedPayerUser}
          selector={selector}
          startIcon={startIcon}
          submenu={submenu}
          text={payerUserButtonLabel}
          transactAs={transactAs}
        />
      )}
      <NoActiveUsersSnackbar
        onClose={handleNoActiveUsersSnackbarClose}
        open={noActiveUsersOpen}
      />
    </>
  );
};

PayerOrPayerUserButton.propTypes = {
  fspId: PropTypes.number,
  onPayerClick: PropTypes.func.isRequired,
  onPayerUserClick: PropTypes.func.isRequired,
  payerAction: PropTypes.object,
  payerButtonText: PropTypes.string.isRequired,
  payerId: PropTypes.number.isRequired,
  payerType: PropTypes.string,
  payerUser: PropTypes.object,
  payerUserAction: PropTypes.object,
  payerUserButtonText: PropTypes.string.isRequired,
  selector: PropTypes.func.isRequired,
  startIcon: PropTypes.element,
  submenu: PropTypes.bool,
  transactAs: PropTypes.bool,
};

PayerOrPayerUserButton.defaultProps = {
  fspId: undefined,
  payerAction: {},
  payerType: null,
  payerUser: null,
  payerUserAction: {},
  startIcon: undefined,
  submenu: false,
  transactAs: false,
};

export default PayerOrPayerUserButton;
